/**
 * ReviewTapHandler - Explicit handler for Review Engine Cards
 * 
 * URL: /standalone/review/{CARD_ID}
 * 
 * This component ONLY handles review engine cards.
 * It calls /api/hotels-standalone/review/{card_id} which ONLY looks in the cards collection.
 * 
 * If the card doesn't exist, it shows an error - it does NOT fall back to room tags.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ReviewTapHandler() {
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get('platform');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tapData, setTapData] = useState(null);

  useEffect(() => {
    const handleTap = async () => {
      try {
        const url = `${API_URL}/api/hotels-standalone/review/${cardId}${platform ? `?platform=${platform}` : ''}`;
        
        // Use fetch with redirect: 'manual' to detect 302 vs error
        const response = await fetch(url, {
          method: 'GET',
          redirect: 'manual'
        });
        
        // Status 0 or 'opaqueredirect' type means we got a cross-origin redirect (302)
        // This is SUCCESS - redirect the browser to the API and let it follow
        if (response.status === 0 || response.type === 'opaqueredirect') {
          window.location.href = url;
          return;
        }
        
        // If we got JSON response, it's an error
        if (response.headers.get('content-type')?.includes('application/json')) {
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.detail || 'Review card not found');
          }
          // If somehow we got a success JSON with redirect_url
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
            return;
          }
        }
        
        // Fallback: redirect to API anyway (let browser handle)
        window.location.href = url;
      } catch (err) {
        console.error('Review tap error:', err);
        setError(err.message || 'Failed to load review card');
        setLoading(false);
      }
    };

    if (cardId) {
      handleTap();
    }
  }, [cardId, platform]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Review Card Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Card ID: {cardId}
            </p>
            <p className="text-xs text-gray-400 mt-4">
              If this is a room check-in tag, use /standalone/room/{cardId} instead.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Redirecting to Review Platform
          </h1>
          <p className="text-gray-600">
            Taking you to leave your review...
          </p>
          {tapData && (
            <div className="mt-4 text-sm text-gray-500">
              <p>Platform: {tapData.platform}</p>
              <ExternalLink className="w-4 h-4 inline ml-2" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
